import React from "react";
import { useParams } from "react-router-dom";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";

import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { PartnersInBranch } from "../../models/PartnersInBranch.ts";
import { getChilitosByBranchAccurate } from "../../services/candidate.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import TableDocuments from "../../components/reports/documentsBranch/TableDocuments.tsx";
import { utils, writeFile } from "xlsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import { formatDate, isValidImageUrl } from "../../utils/index.ts";

const DocumentsBranch = () => {
  useAppSettings();
  const { id } = useParams();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [records, setRecods] = React.useState<PartnersInBranch>();

  const fetchData = async () => {
    try {
      const response = await getChilitosByBranchAccurate(Number(id));
      setRecods(response);
    } catch (error) {
      notification("", "Error al obtener los registros", "danger");
    } finally {
      setLoading(false);
    }
  };

  const exportFile = async () => {
    const ws = utils.aoa_to_sheet([]);

    const headers = [
      "Nombre",
      "Puesto",
      "Fecha de entrada",
      "¿Tiene credenciales?",
      "Documentos pendientes",
    ];
    utils.sheet_add_aoa(ws, [headers], { origin: "A1" });

    let rowIndex = 2;

    for (const record of records?.partners || []) {
      const row = [
        toCamelCase(
          `${record.partner.person.firstName} ${record.partner.person.lastName} ${record.partner.person.secondLastName}`
        ),
        record.partner.candidate.vacancy.position.name,
        formatDate(String(record.recruitmentDate)),
        record.hasCredentials ? "Sí" : "No",
        record.documents.missingDocuments.missingDocumentsInmediate.length +
          record.documents.missingDocuments.missingDocumentsNoInmediate.length,
      ];
      utils.sheet_add_aoa(ws, [row], { origin: `A${rowIndex}` });
      rowIndex++;
    }

    ws["!cols"] = [
      { wch: 35 },
      { wch: 35 },
      { wch: 15 },
      { wch: 16 },
      { wch: 15 },
    ];

    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Data");
    writeFile(wb, "Plantilla de lideres.xlsx");
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  // Calcular estadísticas
  const totalChilitos = records?.partners.length || 0;
  const credencialesGeneradas =
    records?.partners.filter((chilito) => chilito.hasCredentials).length || 0;
  const credencialesFaltantes = totalChilitos - credencialesGeneradas;

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Reportes"
        basePath="/reportes"
        title="Documentos de cada chilito por sucursal"
      />

      <div style={styles.container}>
        <div style={styles.headerSection}>
          <div style={styles.branchHeaderCard}>
            <div style={styles.branchIconWrapper}>
              <i className="fa-solid fa-building" style={styles.branchIcon}></i>
            </div>
            <div>
              <div style={styles.branchLabel}>Sucursal</div>
              <div style={styles.branchName}>
                {records?.branch.name || "Cargando..."}
              </div>
            </div>
          </div>

          <div style={styles.statsGrid}>
            <div style={{ ...styles.statCard, ...styles.statCardPurple }}>
              <div style={styles.statIconWrapper}>
                <i className="fa-solid fa-users" style={styles.statIcon}></i>
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{totalChilitos}</div>
                <div style={styles.statLabel}>Chilitos Total</div>
              </div>
            </div>

            <div style={{ ...styles.statCard, ...styles.statCardGreen }}>
              <div style={styles.statIconWrapper}>
                <i
                  className="fa-solid fa-circle-check"
                  style={styles.statIcon}
                ></i>
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{credencialesGeneradas}</div>
                <div style={styles.statLabel}>Credenciales Generadas</div>
              </div>
            </div>

            <div style={{ ...styles.statCard, ...styles.statCardOrange }}>
              <div style={styles.statIconWrapper}>
                <i className="fa-solid fa-clock" style={styles.statIcon}></i>
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{credencialesFaltantes}</div>
                <div style={styles.statLabel}>Credenciales Faltantes</div>
              </div>
            </div>

            <button style={styles.exportButton} onClick={() => exportFile()}>
              <i
                className="fa-solid fa-file-arrow-down"
                style={styles.exportIcon}
              ></i>
              <span>Exportar Excel</span>
            </button>
          </div>
        </div>

        <div style={styles.tableSection}>
          <div style={styles.tableCard}>
            <div style={styles.tableHeader}>
              <h3 style={styles.tableTitle}>
                <i
                  className="fa-solid fa-file-lines"
                  style={styles.tableTitleIcon}
                ></i>
                Detalle de Documentos
              </h3>
            </div>
            <div style={styles.tableBody}>
              <TableDocuments data={records?.partners || []} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "24px",
    background: "#ffffff",
    minHeight: "calc(100vh - 120px)",
  },
  headerSection: {
    marginBottom: "24px",
  },
  branchHeaderCard: {
    background: "rgba(107, 114, 128, 0.8)",
    backdropFilter: "blur(10px)",
    borderRadius: "12px",
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
    marginBottom: "20px",
    transition: "all 0.3s ease",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  branchIconWrapper: {
    width: "48px",
    height: "48px",
    background: "rgba(255, 255, 255, 0.15)",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(10px)",
  },
  branchIcon: {
    fontSize: "22px",
    color: "#fff",
  },
  branchLabel: {
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.8)",
    fontWeight: "500",
    marginBottom: "2px",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  branchName: {
    fontSize: "20px",
    color: "#fff",
    fontWeight: "700",
    letterSpacing: "-0.5px",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
  },
  statCard: {
    borderRadius: "16px",
    padding: "24px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    cursor: "default",
    position: "relative",
    overflow: "hidden",
  },
  statCardPurple: {
    background: "linear-gradient(135deg, #FF6B6B 0%, #FF4444 100%)",
  },
  statCardGreen: {
    background: "linear-gradient(135deg, #50B7C0 0%, #50834C 100%)",
  },
  statCardOrange: {
    background: "linear-gradient(135deg, #F7B917 0%, #FFA500 100%)",
  },
  statIconWrapper: {
    width: "56px",
    height: "56px",
    background: "rgba(255, 255, 255, 0.25)",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(10px)",
  },
  statIcon: {
    fontSize: "24px",
    color: "#fff",
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#fff",
    lineHeight: "1",
    marginBottom: "6px",
  },
  statLabel: {
    fontSize: "14px",
    color: "rgba(255, 255, 255, 0.9)",
    fontWeight: "500",
    letterSpacing: "0.3px",
  },
  exportButton: {
    background: "linear-gradient(135deg, #50B7C0 0%, #50834C 100%)",
    border: "none",
    borderRadius: "16px",
    padding: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#fff",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 8px 24px rgba(80, 183, 192, 0.3)",
  },
  exportIcon: {
    fontSize: "20px",
  },
  tableSection: {
    marginTop: "24px",
  },
  tableCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    transition: "all 0.3s ease",
  },
  tableHeader: {
    background: "linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%)",
    padding: "20px 28px",
    borderBottom: "1px solid #ffcccc",
  },
  tableTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  tableTitleIcon: {
    fontSize: "22px",
    color: "#FF6B6B",
  },
  tableBody: {
    padding: "28px",
  },
};

// Añadir estilos de hover con CSS-in-JS
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
document.head.appendChild(styleSheet);

export default DocumentsBranch;
